"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import GoogleIcon from "@/assets/images/google.svg?react";
import { signInWithGoogle } from "@/lib/firebase/auth";

import { toast } from "sonner";
import { TRole, TSocialProvider } from "@/actions/types";
import { loginWithSocialProvider } from "@/actions/auth";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useFCMToken } from "@/lib/useFCMToken";
import { postAuthRedirectHref } from "@/lib/localePath";
import type { SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import { useQueryClient } from "@tanstack/react-query";
import { broadcastAuthSessionChangedDeferred } from "@/lib/authSession";

export default function SocialLoginButtons({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const params = useParams();
  const locale: SupportedLanguage = resolveLocale(params.locale as string | undefined);
  const searchParams = useSearchParams();

  const [isLoading, setIsLoading] = useState<TSocialProvider | null>(null);
  const redirectPath = searchParams.get("redirect");
  const role = (searchParams.get("role") || "Personal") as TRole;

  // Get FCM token for push notifications
  const { token: fcmToken } = useFCMToken();

  const handleSocialLogin = async (provider: TSocialProvider) => {
    setIsLoading(provider);

    switch (provider) {
      case "google":
        const result = await signInWithGoogle();

        if ("code" in result) {
          setIsLoading(null);
          toast.error(result.message || "Google authentication failed");
          return;
        } else if (!result.accessToken || !result.idToken) {
          setIsLoading(null);
          toast.error("Google authentication failed");
          return;
        }

        const response = await loginWithSocialProvider(
          {
            provider,
            access_token: result.idToken,
            role,
            deviceToken: fcmToken || undefined,
          },
          redirectPath || "/",
          true // isClient = true since this is called from client component
        );

        if (response.status === "error") {
          setIsLoading(null);
          toast.error(response.message);
          return;
        }

        await queryClient.invalidateQueries({ queryKey: ["myProfile"] });

        // Only call onSuccess if login was successful
        if (onSuccess) {
          onSuccess();
        } else if (locale) {
          router.push(postAuthRedirectHref(locale, redirectPath));
        } else {
          router.push("/");
        }

        broadcastAuthSessionChangedDeferred();
        setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: ["myProfile"] });
        }, 0);

        setIsLoading(null);

        break;

      // Keep these cases for potential future implementation
      case "facebook":
      case "apple":
        toast.error(`${provider} login is not implemented yet`);
        break;
    }
  };

  return (
    <div className="flex justify-center space-x-4">
      {/* <Button
        variant="outline"
        size="icon"
        className="size-12 [&_svg]:!size-8 relative"
        onClick={() => handleSocialLogin("apple")}
        disabled={isLoading !== null}
      >
        <AppleIcon />
        {isLoading === "apple" && (
          <span className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded-full">
            <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </span>
        )}
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="size-12 [&_svg]:!size-8 relative"
        onClick={() => handleSocialLogin("facebook")}
        disabled={isLoading !== null}
      >
        <FacebookIcon />
        {isLoading === "facebook" && (
          <span className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded-full">
            <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </span>
        )}
      </Button> */}
      <Button
        variant="outline"
        size="icon"
        className="size-12 [&_svg]:!size-8 relative"
        onClick={() => handleSocialLogin("google")}
        disabled={isLoading !== null}
      >
        <GoogleIcon />
        {isLoading === "google" && (
          <span className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 rounded-full">
            <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </span>
        )}
      </Button>
    </div>
  );
}
